import clsx from "clsx";
import Image from "next/image";
import { InputHTMLAttributes, forwardRef, memo } from "react";
import s from "./styles.module.sass";

export type InputProps = {
	label: string;
	isEditing?: boolean;
	error?: string;
	success?: boolean;
	minLength?: number;
	maxLength?: number;
	isEditingRequired?: boolean;
	noBorder?: boolean;
	icon?: string;
	color?: "primary" | "secondary";
	onClick?: () => void;
	onBlur?: () => void;
	onEdit?: () => void;
	isError?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;
export type Ref = HTMLInputElement;

const Input = memo(
	forwardRef<Ref, InputProps>(
		(
			{
				label,
				type = "text",
				id,
				disabled,
				defaultValue,
				value,
				placeholder,
				isEditingRequired,
				error,
				success,
				minLength,
				maxLength,
				noBorder,
				color = "primary",
				icon,
				onClick,
				onChange,
				onEdit,
				onBlur,
				name,
				isError,
			},
			ref
		) => {
			const isDisabled = disabled || isEditingRequired;

			const clickEditButton = () => {
				if (onEdit !== undefined) {
					onEdit();
				}
			};

			return (
				<label
					className={clsx(s.wrapper, {
						[s.disabled]: disabled,
					})}
				>
					{label !== "" && (
						<p
							className={clsx(s.label, {
								[s.disabled]: isDisabled,
							})}
						>
							{label}
						</p>
					)}
					<div className={s.inputWrapper}>
						<input
							ref={ref}
							type={type}
							name={name}
							disabled={isDisabled}
							defaultValue={defaultValue}
							id={id}
							value={value}
							minLength={minLength}
							maxLength={maxLength}
							placeholder={placeholder && placeholder}
							className={clsx(s.input, {
								[s.error]: error,
								[s.error]: isError,
								[s.success]: success,
								[s.disabled]: isDisabled,
								[s.noBorder]: noBorder,
								[s.secondaryColor]: color === "secondary",
							})}
							onClick={onClick}
							onBlur={onBlur}
							onChange={onChange}
						/>
						{isEditingRequired && (
							<button className={s.inputIconButton} onClick={clickEditButton}>
								<div className={s.inputIcon} />
							</button>
						)}
						{icon && (
							<div className={s.icon}>
								<Image src={icon} width={30} height={30} />
							</div>
						)}
					</div>
					{error && (
						<p
							className={clsx(s.errorText, {
								[s.secondaryColor]: color === "secondary",
							})}
						>
							{error}
						</p>
					)}
				</label>
			);
		}
	)
);

Input.displayName = "Input";

export default Input;
